import { EntryConfig } from "@/types/entryConfig";
import { MeasurementMetric, getAvailableMetrics } from "@/types/measurementMetrics";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface MeasurementMetricTabsProps {
  entryConfig: EntryConfig;
  selectedMetric: MeasurementMetric;
  onMetricChange: (metric: MeasurementMetric) => void;
}

/**
 * Shared component for rendering metric selection tabs for measurement types
 * (imageMeasurement, standCountMeasurement, and rowPlantedStandCount).
 *
 * Dynamically renders tabs based on the entry config type and available metrics.
 */
export function MeasurementMetricTabs({
  entryConfig,
  selectedMetric,
  onMetricChange
}: MeasurementMetricTabsProps) {
  // Only render for measurement types
  if (
    entryConfig.type !== "imageMeasurement" &&
    entryConfig.type !== "standCountMeasurement" &&
    entryConfig.type !== "rowPlantedStandCount"
  ) {
    return null;
  }

  const availableMetrics = getAvailableMetrics(entryConfig);

  // Don't render if no metrics available
  if (availableMetrics.length === 0) {
    return null;
  }

  return (
    <div className="mb-4">
      <ToggleGroup
        type="single"
        value={selectedMetric}
        onValueChange={(value) => {
          if (value) {
            onMetricChange(value as MeasurementMetric);
          }
        }}
        className="justify-start"
      >
        {availableMetrics.map((metric) => (
          <ToggleGroupItem key={metric.value} value={metric.value}>
            {metric.label} {metric.unit && `(${metric.unit})`}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
